import React from "react";
import { MathJax } from "better-react-mathjax";

export function NewtonIntro({ lite = false }) {
  const baseClass = lite
    ? 'space-y-2 text-justify hyphens-auto text-sm leading-relaxed text-white/80 md:text-base'
    : 'space-y-4 text-justify hyphens-auto leading-relaxed text-white/85 text-sm md:text-base';
  return (
    <div className={baseClass}>
      Solving nonlinear equations is a core problem in applied mathematics. Given a function
      <MathJax>{String.raw`\[
        f\colon \mathbb{R} \to \mathbb{R} \text{ or more generally } f\colon \mathbb{R}^n \to \mathbb{R}^n,
      \]`}</MathJax>
      we seek a solution <MathJax inline>{"\\( x^* \\)"}</MathJax> such that <MathJax inline>{"\\( f(x^*) = 0 \\)"}</MathJax>.
      Newton’s method (also known as the Newton–Raphson method) is the classical method to solve such problems. It combines local 
      linearization with iteration, and when it works it converges extremely fast (quadratically) when we get close to the solution. 
      In many practical problems, Newton’s method is the backbone of numerical algorithms, especially in fields like simulation, 
      optimization, inverse problems, and data science. Its efficiency and generality make it indispensable despite its limitations, 
      and many modern algorithms can be viewed as safeguarded or generalized variants of Newton’s method. Understanding its strengths 
      and pitfalls is therefore essential for both theoretical analysis and practical computation.
    </div>
  );
}

export default function NewtonLongExplanation() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-white/80 text-justify hyphens-auto md:text-base">
      <h2 className="text-sm font-semibold md:text-base">1. Introduction and Motivation</h2>
      <p>Why do we care about root finding? Because it appears everywhere:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Physics and Engineering:</strong> Nonlinear constitutive laws, equilibrium conditions, 
        and discretized nonlinear PDEs often lead to problems of the form <MathJax inline>{"\\( f(x)=0 \\)"}</MathJax>.</li>
        <li><strong>Optimization:</strong> Stationary points satisfy <MathJax inline>{"\\( \\nabla f(x)=0 \\)"}</MathJax>; Newton’s 
        method applied to the gradient is at the heart of second-order methods.</li>
        <li><strong>Statistics & ML:</strong> Maximum likelihood solves score equations <MathJax inline>{"\\( \\nabla \\ell(\\theta)=0 \\)"}</MathJax>; 
        logistic regression and GLMs often use Newton's method.</li>
        <li><strong>Numerical Inversion:</strong> Solving <MathJax inline>{"\\( g(x)=y \\)"}</MathJax> reduces to roots of <MathJax inline>{"\\( f(x)=g(x)-y \\)"}</MathJax>.</li>
      </ul>
      <p>
        Newton’s method is fundamental in numerical root-finding (more general solving nonlear problems), as it typically exhibits quadratic convergence in
        the neighborhood of a simple root, with the number of correct digits approximately doubling at each iteration.
        Despite its reliance on derivative evaluations and the necessity of a sufficiently good initial approximation—often
        accompanied by safeguarding in practice—there are few genuine alternatives that combine its efficiency and general applicability.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">2. Derivation (1D) and the Newton Step</h2>
      <p>
        Suppose <MathJax inline>{"\\( f: \\mathbb{R}\\to\\mathbb{R} \\)"}</MathJax> is differentiable and we want <MathJax inline>{"\\( f(x)=0 \\)"}</MathJax>.
        Linearize <MathJax inline>{"\\( f \\)"}</MathJax> at the current iterate <MathJax inline>{"\\( x_k \\)"}</MathJax>:
      </p>
      <MathJax>{String.raw`\[
        f(x) \approx f(x_k) + f'(x_k)\,(x - x_k).
      \]`}</MathJax>
      <p>
        Set this approximation to zero and solve for the next iterate:
      </p>
      <MathJax>{String.raw`\[
        \begin{aligned}
        0 = f(x_k) + f'(x_k)\,(x_{k+1} - x_k) \\ \quad \Rightarrow \quad
        x_{k+1} = x_k - \frac{f(x_k)}{f'(x_k)}.
        \end{aligned}
      \]`}</MathJax>
      <p>
        This is the <em>Newton update</em>. It can be interpreted geometrically: the tangent line at
        <MathJax inline>{"\\( (x_k, f(x_k)) \\)"}</MathJax> intersects the <MathJax inline>{"\\( x \\)"}</MathJax>-axis at <MathJax inline>{"\\( x_{k+1} \\)"}</MathJax>.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">3. Convergence Theory (1D)</h2>
      <h3 className="font-semibold mt-2">Simple vs. Multiple Roots</h3>
      <p>
        A root <MathJax inline>{"\\( x^* \\)"}</MathJax> is <em>simple</em> if <MathJax inline>{"\\( f(x^*)=0 \\)"}</MathJax> and <MathJax inline>{"\\( f'(x^*)\\neq 0 \\)"}</MathJax>.
        If <MathJax inline>{"\\( f'(x^*)=0 \\)"}</MathJax> but higher derivatives are nonzero, the root has multiplicity <MathJax inline>{"\\( m>1 \\)"}</MathJax>.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Simple root:</strong> If <MathJax inline>{"\\( f \\)"} </MathJax> is <MathJax inline>{"\\( C^2 \\)"}</MathJax> near <MathJax inline>{"\\( x^* \\)"}</MathJax> and <MathJax inline>{"\\( f'(x^*)\\neq 0 \\)"}</MathJax>, then for <MathJax inline>{"\\( x_0 \\)"} </MathJax> close enough, Newton converges <em>quadratically</em>:
          <MathJax>{String.raw`\[
            \lvert e_{k+1}\rvert \le C\,\lvert e_k\rvert^2 \quad \text{with } e_k:=x_k-x^*.
          \]`}</MathJax>
        </li>
        <li><strong>Multiple root:</strong> If <MathJax inline>{"\\( f(x^*)=f'(x^*)= \\cdots =f^{(m-1)}(x^*)=0 \\)"} </MathJax> and <MathJax inline>{"\\( f^{(m)}(x^*) \\neq 0 \\)"} </MathJax>, then convergence is typically <em>linear</em>.
        </li>
      </ul>

      <h3 className="font-semibold mt-2">Local Error Analysis</h3>
      <p>
        Assume <MathJax inline>{"\\( f \\)"} </MathJax> is <MathJax inline>{"\\( C^2 \\)"}</MathJax> and <MathJax inline>{"\\( f'(x^*)\\neq 0 \\)"}</MathJax>. Taylor expand around <MathJax inline>{"\\( x^* \\)"}</MathJax>:
      </p>
      <MathJax>{String.raw`\[
        f(x_k) = f'(x^*) e_k + \tfrac12 f''(x^*) e_k^2 + \mathcal{O}(e_k^3).
      \]`}</MathJax>
      <p>
        Using <MathJax inline>{"\\( x_{k+1}=x_k - f(x_k)/f'(x_k) \\)"}</MathJax> and expanding the denominator gives
      </p>
      <MathJax>{String.raw`\[
        e_{k+1} = -\frac{f''(x^*)}{2 f'(x^*)}\, e_k^2 + \mathcal{O}(e_k^3),
      \]`}</MathJax>
      <p>which shows quadratic convergence and yields the leading error constant.</p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">4. The Newton Algorithm (1D)</h2>
      <ol className="list-decimal pl-6 space-y-1">
        <li>Choose an initial guess <MathJax inline>{"\\( x_0 \\)"}</MathJax>. (Good initialization matters.)</li>
        <li>For <MathJax inline>{"\\( k=0,1,2,\\dots \\)"}</MathJax>:
          <ul className="list-disc pl-6">
            <li>Evaluate <MathJax inline>{"\\( f(x_k) \\)"} </MathJax> and <MathJax inline>{"\\( f'(x_k) \\)"} </MathJax>.</li>
            <li>If <MathJax inline>{"\\( f'(x_k)=0 \\)"} </MathJax>, <em>pivot</em>: adjust the step (damping) or pick a new initial guess.</li>
            <li>Compute the step <MathJax inline>{"\\( \\Delta x_k = - \\dfrac{f(x_k)}{f'(x_k)} \\)"} </MathJax> 
            and update <MathJax inline>{"\\( x_{k+1}=x_k+ \\Delta x_k \\)"} </MathJax>.</li>
          </ul>
        </li>
        <li>Stop when a criterion is met (see below).</li>
      </ol>

      <h3 className="font-semibold mt-2">Stopping Criteria</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Residual small:</strong> <MathJax inline>{"\\( |f(x_k)| \\le \\varepsilon_{\\text{res}} \\)"} </MathJax>.</li>
        <li><strong>Step small:</strong> <MathJax inline>{"\\( |x_{k+1}-x_k| \\le \\varepsilon_{\\text{step}}(1+|x_k|) \\)"} </MathJax>.</li>
        <li><strong>Max iterations:</strong> safety cap to avoid infinite loops.</li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">5. Practical Considerations & Safeguards</h2>
      <p>
        Several practical issues arise in the application of Newton’s method. If the derivative is small, i.e.
        <MathJax inline>{"\(|f'(x_k)|\)"}</MathJax> is close to zero, the Newton step can become excessively large; in such cases, damping is often applied, replacing the update by
        <MathJax>{String.raw`\[
         x_{k+1} = x_k - \alpha \dfrac{f(x_k)}{f'(x_k)}, \quad 0 < \alpha \leq 1.
        \]`}</MathJax>
        To improve global convergence, one may employ line search strategies that reduce <MathJax inline>{"|f(x)|"}</MathJax>
        (or a suitable merit function), or combine Newton iterations with bracketing schemes such as bisection or the
        secant method to guarantee progress. In the presence of multiple roots, the convergence rate deteriorates
        from quadratic to linear; however, if the root multiplicity <MathJax inline>{" m "}</MathJax> is known, the modified iteration
        <MathJax>{String.raw`\[
         x_{k+1} = x_k - m \dfrac{f(x_k)}{f'(x_k)}
        \]`}</MathJax>
        restores quadratic convergence.
        For non-differentiable or highly oscillatory functions, derivative-free alternatives such as the secant method may
        be more appropriate. Finally, one must be aware that Newton’s method exhibits distinct basins of attraction:
        starting far from a root may lead to divergence or even cycling.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">7. 1D Example</h2>
      <p><strong>Example (Unique simple root).</strong> Solve <MathJax inline>{"\\( f(x)=x^3-2=0 \\)"} </MathJax>.</p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        f'(x)&=3x^2,\\
         x_{k+1}&=x_k - \frac{x_k^3-2}{3x_k^2}
        = \frac{2x_k}{3} + \frac{2}{3x_k^2}.
        \end{aligned}
      \]`}</MathJax>
      <p>Start with <MathJax inline>{"\\( x_0=1 \\)"} </MathJax>:</p>
      <MathJax>{String.raw`\[
        \begin{aligned}
          x_1 &= 1 - \frac{1-2}{3} = \tfrac{4}{3} \approx 1.3333,\\
          x_2 &= \tfrac{2}{3}\cdot \tfrac{4}{3} + \frac{2}{3}\cdot \frac{1}{(4/3)^2}
               = \tfrac{8}{9} + \tfrac{2}{3}\cdot \tfrac{9}{16} \\
               &= \tfrac{8}{9} + \tfrac{3}{8}
               = \tfrac{64}{72} + \tfrac{27}{72}
               = \tfrac{91}{72} \approx 1.2639,\\
          x_3 &\approx 1.2599,\\
          x_4 &\approx 1.25992105\ (\text{correct to many digits}).
        \end{aligned}
      \]`}</MathJax>

      <h2 className="text-sm font-semibold mt-4 md:text-base">8. Newton’s Method for Systems</h2>
      <p>
        Now <MathJax inline>{"\\( f: \\mathbb{R}^n \\to \\mathbb{R}^n \\)"} </MathJax> and we seek <MathJax inline>{"\\( f(x)=0 \\)"} </MathJax>.
        Linearize at <MathJax inline>{"\\( x_k \\)"} </MathJax> using the Jacobian <MathJax inline>{"\\( J_f(x_k) \\in \\mathbb{R}^{n\\times n} \\)"} </MathJax>:
      </p>
      <MathJax>{String.raw`\[
        f(x_k + \Delta x) \approx f(x_k) + J_f(x_k)\, \Delta x.
      \]`}</MathJax>
      <p>Set the linear model to zero and solve the <em>Newton system</em> for the step <MathJax inline>{"\\( \\Delta x_k \\)"} </MathJax>:</p>
      <MathJax>{String.raw`\[
        J_f(x_k)\, \Delta x_k = -\, f(x_k), \qquad x_{k+1} = x_k + \Delta x_k.
      \]`}</MathJax>

      <h3 className="font-semibold mt-2">Convergence (Systems)</h3>
      <p>
        If <MathJax inline>{"\\( f \\)"} </MathJax> is <MathJax inline>{"\\( C^1 \\)"} </MathJax> near a root <MathJax inline>{"\\( x^* \\)"} </MathJax>,
        the Jacobian is nonsingular at <MathJax inline>{"\\( x^* \\)"} </MathJax>, and <MathJax inline>{"\\( x_0 \\)"} </MathJax> is sufficiently close, then the method converges quadratically:
      </p>
      <MathJax>{String.raw`\[
        \|x_{k+1}-x^*\| \le C\,\|x_k-x^*\|^2.
      \]`}</MathJax>

      <h3 className="font-semibold mt-2">Implementation Details</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Forming and solving the Newton system:</strong> Usually solved by Gaussian elimination with pivoting or an LU factorization of <MathJax inline>{"\\( J_f(x_k) \\)"} </MathJax>.</li>
        <li><strong>Cost:</strong> For dense problems, factoring <MathJax inline>{"\\( n\\times n \\)"} </MathJax> Jacobians costs about <MathJax inline>{"\\( \\tfrac{2}{3}n^3 \\)"} </MathJax> flops per iteration, plus <MathJax inline>{"\\( \\mathcal{O}(n^2) \\)"} </MathJax> for solves.</li>
        <li><strong>Reuse (Inexact Newton):</strong> For slowly changing Jacobians, reuse or update factorizations (quasi-Newton/Broyden) to reduce cost.</li>
        <li><strong>Globalization:</strong> Damped Newton with line search or trust regions ensures progress from poor initial guesses.</li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">9. 2D Example</h2>
      <p><strong>2D System.</strong> Solve for <MathJax inline>{"\\( (x,y) \\)"} </MathJax>:</p>
      <MathJax>{String.raw`\[
        \begin{aligned}
        f_1(x,y)&=x^2 + y^2 - 5 = 0,\\
        f_2(x,y)&=x - y - 1 = 0.
        \end{aligned}
      \]`}</MathJax>
      <p>We have</p>
      <MathJax>{String.raw`\[
        f(x,y)=\begin{bmatrix} x^2+y^2-5 \\ x-y-1 \end{bmatrix},\quad
        J_f(x,y)=\begin{bmatrix} 2x & 2y \\ 1 & -1 \end{bmatrix}.
      \]`}</MathJax>
      <p>Start at <MathJax inline>{"\\( ({x_0}_1,{x_0}_2)=(2,0) \\)"} </MathJax>. Then</p>
      <MathJax>{String.raw`\[
        f({x_0}_1,{x_0}_2)=\begin{bmatrix} -1 \\ 1 \end{bmatrix},\quad
        J_f({x_0}_1,{x_0}_2)=\begin{bmatrix} 4 & 0 \\ 1 & -1 \end{bmatrix}.
      \]`}</MathJax>
      <p>Solve <MathJax inline>{"\\( J_f({x_0}_1,{x_0}_2) \\Delta {x_0} = -f \\)"} </MathJax>:</p>
      <MathJax>{String.raw`\[
        \begin{aligned}
        &\begin{bmatrix} 4 & 0 \\ 1 & -1 \end{bmatrix}
        \begin{bmatrix} \Delta {{x_0}_1} \\ \Delta {{x_0}_1} \end{bmatrix}
        =
        \begin{bmatrix} 1 \\ -1 \end{bmatrix}\\
        & \Rightarrow
        4 \Delta {{x_0}_1}  = 1 \Rightarrow s_x=\tfrac{1}{4},\quad
        \Delta {{x_0}_1} - \Delta {{x_0}_1} = -1 \\
        &\Rightarrow \Delta {{x_0}_1} = \Delta {{x_0}_1}  + 1 = \tfrac{5}{4}.
        \end{aligned}
      \]`}</MathJax>
      <p>Update:</p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        (x_1,y_1)&=(2,0)+\left(\tfrac{1}{4},\tfrac{5}{4}\right)\\
        &=\left(\tfrac{9}{4}, \tfrac{5}{4}\right)\\
        &=(2.25,1.25).
        \end{aligned}
      \]`}</MathJax>
      <p>Next iteration (abbreviated): evaluate</p>
      <MathJax>{String.raw`\[
        \begin{aligned}
        f(x_1,y_1)&=
        \begin{bmatrix}
          (2.25)^2+(1.25)^2-5\\[2pt]
          2.25-1.25-1
        \end{bmatrix}\\
        &=
        \begin{bmatrix}
          5. +  (\text{small}) -5\\[2pt]
          0
        \end{bmatrix}
        =
        \begin{bmatrix}
          0.3125\\ 0
        \end{bmatrix},\\
        J_f(x_1,y_1)&=\begin{bmatrix} 4.5 & 2.5 \\ 1 & -1 \end{bmatrix}.
        \end{aligned}
      \]`}</MathJax>
      <p>Solve <MathJax inline>{"\\( J_f(x_1,y_1) \\Delta x_1=-f \\)"} </MathJax> to get a smaller correction (details omitted). The method rapidly approaches the intersection of the circle and the line.</p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">10. Short Worked Example with Damping</h2>
      <p>Consider <MathJax inline>{"\\( f(x)=\\cos x - x \\)"} </MathJax> with <MathJax inline>{"\\( x_0=0.5 \\)"} </MathJax>.</p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        f'(x)&=-\sin x - 1,\\
        x_{k+1}&=x_k - \frac{\cos x_k - x_k}{-\sin x_k -1}.
        \end{aligned}
      \]`}</MathJax>
      <p>
        If a full step increases <MathJax inline>{"\\( |f| \\)"} </MathJax>, pick <MathJax inline>{"\\( \\alpha\\in(0,1) \\)"} </MathJax> and set
        <MathJax inline>{"\\( x_{k+1}=x_k + \\alpha s_k \\)"} </MathJax>. In practice only a few damped steps are needed; once close, take full steps and enjoy quadratic convergence to the unique fixed point of <MathJax inline>{"\\( \\cos x \\)"} </MathJax>.
      </p>


      <h2 className="text-sm font-semibold mt-4 md:text-base">11. Variants and Extensions</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Damped/Line-Search Newton:</strong> <MathJax inline>{"\\( x_{k+1}=x_k + \\alpha_k \\Delta x_k \\)"} </MathJax> with <MathJax inline>{"\\( 0<\\alpha_k\\le 1 \\)"} </MathJax> chosen to reduce a merit function (e.g., <MathJax inline>{"\\( \\tfrac12\\|f(x)\\|^2 \\)"} </MathJax>).</li>
        <li><strong>Trust-Region Newton:</strong> Restricts <MathJax inline>{"\\( \\|\\Delta x_k\\| \\le \\varepsilon_k \\)"} </MathJax> and solves a constrained subproblem for robustness.</li>
        <li><strong>Quasi-Newton (Broyden):</strong> Avoids forming the true Jacobian; updates an approximate Jacobian using secant conditions.</li>
      </ul>



      <h2 className="text-sm font-semibold mt-4 md:text-base">12. Appendix: Newton for Optimization</h2>
      <p>
        To minimize <MathJax inline>{"\\( \\varphi(x) \\)"} </MathJax>, solve <MathJax inline>{"\\( \\nabla\\varphi(x)=0 \\)"} </MathJax>.
        Newton’s step solves <MathJax inline>{"\\( \\nabla^2 \\varphi(x_k) s_k = -\\nabla\\varphi(x_k) \\)"} </MathJax>.
        With a positive-definite Hessian and line search, this yields rapid local convergence to a (strict) minimizer.
      </p>
    </div>
  );
}
